/**
 * Keyboard review loop for the queue — §15.1.
 *
 * An SDR approving 30–60 drafts a day was doing it entirely with the mouse:
 * click row, read, click Approve, click the next row. That is 40–80 clicks a day
 * that carry no decision, and it is the interaction both UX reviews put first.
 *
 * The rules that matter more than the bindings:
 *
 *   - Nothing fires while the rep is typing. The steer box and the body editor
 *     consume `j`, `e`, `r`, `s` and `x` as literal characters, and a shortcut
 *     that mangles a draft mid-sentence would end the feature's life on day one.
 *   - Nothing fires while a dialog is open. The enrollment wizard and the
 *     account drawer mark the background `inert` and lay a scrim over it; the
 *     queue underneath must be as dead to the keyboard as it is to the mouse.
 *   - Space and Enter defer to a focused control. Otherwise a rep whose focus
 *     sits on the Approve button would send TWO approvals with one press — the
 *     browser's native activation plus ours.
 *   - Modified chords are left alone, so ⌘K, Ctrl+R and friends keep working.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { QueueActions } from './queueActions';

/** Marks a queue row in the DOM: both the Space guard and scrolling need it. */
export const TASK_ROW_ATTR = 'data-task-row';

export interface QueueKeymapOptions {
  /** Task ids in the order they are rendered, top to bottom. */
  order: number[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Live handlers published by whichever pane is mounted. */
  actions: RefObject<QueueActions>;
  onShowHelp: () => void;
}

/**
 * Is the keystroke destined for a text field?
 *
 * `isContentEditable` covers the WYSIWYG editor whenever it lands — the current
 * body field is a plain textarea, but this must not need revisiting for that.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Is a modal or drawer covering the queue?
 *
 * Detected by the scrim rather than by a prop, because every dialog in the app
 * goes through the one `Dialog` shell and renders one of these — so this stays
 * true for dialogs that do not exist yet.
 */
function isDialogOpen(): boolean {
  return document.querySelector('.modal-scrim, .drawer-scrim') !== null;
}

/**
 * Would the browser itself act on this key, given what is focused?
 *
 * Space and Enter activate a focused button or link natively. The one exception
 * is a task row: re-selecting the row that is already selected is a no-op, so
 * letting the shortcut through there costs nothing and is what makes
 * "click a row, then drive with the keyboard" work at all.
 */
function focusWouldSwallowActivation(): boolean {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return false;
  if (active.closest(`[${TASK_ROW_ATTR}]`)) return false;

  const tag = active.tagName;
  return tag === 'BUTTON' || tag === 'A' || active.getAttribute('role') === 'button';
}

export function scrollRowIntoView(taskId: number): void {
  const row = document.querySelector<HTMLElement>(`[${TASK_ROW_ATTR}="${taskId}"]`);
  // `nearest` rather than `center`: the rep's eye is already on this list, and
  // yanking it to the middle on every step is its own kind of motion sickness.
  row?.scrollIntoView({ block: 'nearest' });
}

const AUTO_ADVANCE_KEY = 'actos.autoAdvance';

/**
 * Whether Approve should move to the next row — a per-device preference.
 *
 * Deliberately `localStorage` and not the profile API: every other setting in
 * `Settings.tsx` is a server-persisted business rule (sending hours, timezone)
 * backed by a column, and this is a keyboard habit. Storing it server-side would
 * mean a migration for a toggle that reasonably differs between a rep's laptop
 * and the shared machine in the office.
 *
 * Defaults ON — the auto-advance is the point of the feature — and survives a
 * storage read throwing, which it does in a locked-down browser profile.
 */
export function useAutoAdvancePreference(): [boolean, (next: boolean) => void] {
  const [enabled, setEnabled] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(AUTO_ADVANCE_KEY) !== 'off';
    } catch {
      return true;
    }
  });

  const set = useCallback((next: boolean) => {
    setEnabled(next);
    try {
      window.localStorage.setItem(AUTO_ADVANCE_KEY, next ? 'on' : 'off');
    } catch {
      // A preference we cannot persist is still a preference for this session.
    }
  }, []);

  return [enabled, set];
}

/**
 * Move to the row that followed the one just approved.
 *
 * The successor is snapshotted when the SELECTION changes, not when the approve
 * resolves. By the time an approve lands, the board has reloaded and the
 * approved row has moved to the Scheduled lane — so "the row after it" computed
 * then would walk the rep into a different lane entirely.
 */
export function useAutoAdvance({
  order,
  selectedId,
  onSelect,
  enabled,
}: {
  order: number[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  enabled: boolean;
}): () => void {
  const successor = useRef<number | null>(null);
  const orderRef = useRef<number[]>(order);
  // Assigned during render, as `Dialog` does with its focus-restore target: the
  // effect below needs the order as it stands now, not one commit behind.
  orderRef.current = order;

  useEffect(() => {
    const current = orderRef.current;
    const at = selectedId === null ? -1 : current.indexOf(selectedId);
    successor.current = at >= 0 ? (current[at + 1] ?? null) : null;
    // Keyed on the selection only: a background poll reshuffling the board must
    // not silently repoint where Approve is about to send the rep.
  }, [selectedId]);

  return useCallback(() => {
    if (!enabled) return;
    const target = successor.current;
    // Only follow a row that still exists — it may have been sent, skipped or
    // filtered away while the rep was reading.
    if (target === null || !orderRef.current.includes(target)) return;
    onSelect(target);
    scrollRowIntoView(target);
  }, [enabled, onSelect]);
}

export function useQueueKeymap({
  order,
  selectedId,
  onSelect,
  actions,
  onShowHelp,
}: QueueKeymapOptions): void {
  const step = useCallback(
    (delta: number) => {
      if (order.length === 0) return;

      const at = selectedId === null ? -1 : order.indexOf(selectedId);
      // Nothing selected: J opens the top of the queue, K the bottom.
      const next =
        at === -1
          ? delta > 0
            ? 0
            : order.length - 1
          : Math.min(order.length - 1, Math.max(0, at + delta));

      const id = order[next];
      if (id === undefined || id === selectedId) return;
      onSelect(id);
      scrollRowIntoView(id);
    },
    [order, selectedId, onSelect],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (isDialogOpen()) return;

      const run = (action?: { run: () => void }) => {
        if (!action) return;
        event.preventDefault();
        action.run();
      };

      switch (event.key) {
        case 'j':
        case 'J':
        case 'ArrowDown':
          event.preventDefault();
          step(1);
          return;

        case 'k':
        case 'K':
        case 'ArrowUp':
          event.preventDefault();
          step(-1);
          return;

        case ' ':
        case 'Enter':
          if (focusWouldSwallowActivation()) return;
          run(actions.current?.primary);
          return;

        case 'e':
        case 'E':
          run(actions.current?.edit);
          return;

        case 'r':
        case 'R':
          run(actions.current?.regenerate);
          return;

        case 's':
        case 'S':
          run(actions.current?.snooze);
          return;

        case 'x':
        case 'X':
          run(actions.current?.skip);
          return;

        case '?':
          event.preventDefault();
          onShowHelp();
          return;

        default:
          return;
      }
    };

    /*
     * Bubble phase, not capture. `Dialog` and `tips.ts` bind Escape in the
     * capture phase specifically to get first refusal; binding here in capture
     * too would put this handler in a race with them for no benefit.
     */
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, actions, onShowHelp]);
}
